import { type FC, Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import "./Overview.scss";

const TAB_IDS = ["all-campaigns", "email-campaigns", "whatsapp-campaigns"] as const;

type EngagementOverviewTabId = (typeof TAB_IDS)[number];

const AllCampaignsTab = lazy(() => import("./Tabs/AllCampaigns/AllCampaignsTab"));
const EmailCampaignsTab = lazy(() => import("./Tabs/EmailCampaigns/EmailCampaignsTab"));
const WhatsAppCampaignsTab = lazy(
  () => import("./Tabs/WhatsAppCampaigns/WhatsAppCampaignsTab"),
);

const tabDomId = (id: EngagementOverviewTabId) => `engagement-overview-tab-${id}`;
const panelDomId = (id: EngagementOverviewTabId) => `engagement-overview-panel-${id}`;

const TAB_LABEL_KEYS: Record<EngagementOverviewTabId, string> = {
  "all-campaigns": "engagementOverview.tabAll",
  "email-campaigns": "engagementOverview.tabEmail",
  "whatsapp-campaigns": "engagementOverview.tabWhatsApp",
};

const Overview: FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<EngagementOverviewTabId>("all-campaigns");

  return (
    <div className="engagement-overview-dashboard-with-data engagement-overview-dashboard-with-data--viewport-bound">
      <div className="engagement-overview-tabs">
        <div className="engagement-overview-tabs__tabs" role="tablist" aria-label={t("engagementOverview.tabsAria")}>
          {TAB_IDS.map(tabId => (
            <button
              key={tabId}
              id={tabDomId(tabId)}
              type="button"
              role="tab"
              aria-selected={activeTab === tabId}
              aria-controls={panelDomId(tabId)}
              className={
                activeTab === tabId
                  ? "engagement-overview-tabs__tab engagement-overview-tabs__tab--active"
                  : "engagement-overview-tabs__tab"
              }
              onClick={() => setActiveTab(tabId)}
            >
              {t(TAB_LABEL_KEYS[tabId])}
            </button>
          ))}
        </div>

        <div className="engagement-overview-tabs__content">
          <Suspense
            fallback={
              <div className="engagement-overview-tabs__loading" role="status" aria-live="polite">
                <CircularLoader size={32} thickness={4} />
              </div>
            }
          >
            <div
              id={panelDomId("all-campaigns")}
              role="tabpanel"
              aria-labelledby={tabDomId("all-campaigns")}
              hidden={activeTab !== "all-campaigns"}
            >
              {activeTab === "all-campaigns" ? <AllCampaignsTab /> : null}
            </div>
            <div
              id={panelDomId("email-campaigns")}
              role="tabpanel"
              aria-labelledby={tabDomId("email-campaigns")}
              hidden={activeTab !== "email-campaigns"}
            >
              {activeTab === "email-campaigns" ? <EmailCampaignsTab /> : null}
            </div>
            <div
              id={panelDomId("whatsapp-campaigns")}
              role="tabpanel"
              aria-labelledby={tabDomId("whatsapp-campaigns")}
              hidden={activeTab !== "whatsapp-campaigns"}
            >
              {activeTab === "whatsapp-campaigns" ? <WhatsAppCampaignsTab /> : null}
            </div>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default Overview;
